'use client'
import {ChangeEvent, useContext, useMemo, useEffect} from 'react'
import {Calendar, Select} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {StayContext} from '@local/stores/contexts'
import PersonSVG from '@local/assets/svgs/icon-person.svg'
import BedSVG from '@local/assets/svgs/icon-bed.svg'
import FoodSVG from '@local/assets/svgs/icon-food.svg'

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

const guestOptions = Array.from({length: 10}, (_, i) => ({
	label: `${i + 1}`,
	value: `${i + 1}`,
}))

type BookMenuProps = {
	rooms: Array<{
		title: string;
		name: string;
		packages: Array<{
			nightCount: number;
			title: string;
			_key: string;
		}>;
	}>;
};

export const BookMenu = ({
	rooms,
}: BookMenuProps) => {
	const {roomPackage, room, packageNightCount, date, guests, setDate, setRoom, setGuests, setRoomPackage, setPackageNightCount} = useContext(StayContext)

	const roomOptions = rooms.map((room) => ({
		label: room.title,
		value: room.name,
	}))

	const selectedRoomPackages = useMemo(() => {
		return rooms.find((r) => r.name === room)?.packages
	}, [room, rooms]) || []

	const maxDate = useMemo(() => {
		if (packageNightCount === null) {
			return new Date(new Date().setDate(new Date().getDate() + 365))
		}
		if (date && Array.isArray(date) && date.length > 0 && date[0]) {
			try {
				const startDate = new Date(date[0])

				if (isNaN(startDate.getTime())) {
					return new Date(new Date().setDate(new Date().getDate() + 3))
				}

				const endDate = new Date(startDate)

				endDate.setDate(startDate.getDate() + 3)

				return endDate
			} catch (e) {
				return new Date(new Date().setDate(new Date().getDate() + 3))
			}
		}
		return new Date(new Date().setDate(new Date().getDate() + 365))
	}, [packageNightCount, date])

	const calendarValue = useMemo(() => {
		if (!date) return null

		if (Array.isArray(date) && date.length > 0) {
			try {
				const dateObjects = date.filter(d => d !== null).map(d => new Date(d))

				if (dateObjects.length === 2 && !isNaN(dateObjects[0].getTime()) && !isNaN(dateObjects[1].getTime())) {
					return dateObjects as [Date, Date]
				} else if (dateObjects.length === 1 && !isNaN(dateObjects[0].getTime())) {
					return dateObjects[0]
				}
			} catch (e) {
				return null
			}
		}

		return null
	}, [date])

	const handleDateChange = (value: Value) => {
		if (!value) {
			setDate([])
			return
		}

		if (Array.isArray(value)) {
			if (value[0] && value[1]) {
				setDate([
					value[0].toISOString(),
					value[1].toISOString(),
				])
			} else if (value[0] && !value[1]) {
				setDate([value[0].toISOString()])
			}
		} else if (value) {
			setDate([value.toISOString()])
		}
	}

	useEffect(() => {
		const selectedPackage = selectedRoomPackages.find((pkg) => pkg._key === roomPackage)
		if (selectedPackage) {
			if (selectedPackage?.totalPrice?.nightCount) {
				setPackageNightCount(selectedPackage.totalPrice.nightCount)
			}
		}
	}, [roomPackage])

	return (
		<div className='flex flex-col gap-y-10'>

			<Select
				key={`room-select-${room}`}
				theme='light'
				name='room'
				id='product-hero-room-select'
				placeholder='Room Type'
				onChange={(e: ChangeEvent<HTMLSelectElement>) => setRoom(e.target.value)}
				options={roomOptions}
				value={room || ''}
				containerClassName='border-0 w-full'
				className={cx('border-0 border-r overflow-hidden capitalize')}
				keepPrefixIcon
				disablePlaceholder={false}
				titleClassName='text-11 tracking-wide font-normal overflow-clip overflow-ellipsis whitespace-nowrap uppercase text-ellipsis'
				prefixIcon={<BedSVG className='w-16 h-16 mr-4 shrink-0' />} />

			<Select
				key={`package-select-${room}`}
				theme='light'
				name='package'
				id='product-hero-package-select'
				placeholder='Package'
				onChange={(e: ChangeEvent<HTMLSelectElement>) => setRoomPackage(e.target.value)}
				options={selectedRoomPackages.map((pkg) => ({
					label: pkg.title,
					value: pkg._key,
				}))}
				value={roomPackage || ''}
				disablePlaceholder={false}
				containerClassName='border-0 w-full'
				className={cx('border-0 border-r overflow-hidden capitalize')}
				keepPrefixIcon
				titleClassName='text-11 tracking-wide font-normal overflow-clip overflow-ellipsis whitespace-nowrap uppercase text-ellipsis'
				prefixIcon={<FoodSVG className='w-16 h-16 mr-4 shrink-0' />} />

			<Select
				key={`guest-select-${guests}`}
				theme='light'
				name='guest'
				id='product-hero-guest-select'
				placeholder='Guests'
				onChange={(e: ChangeEvent<HTMLSelectElement>) => setGuests(e.target.value)}
				options={guestOptions}
				value={guests || ''}
				containerClassName='border-0 w-full'
				className='border-0 border-r !rounded-none'
				keepPrefixIcon
				prefixIcon={<PersonSVG className='w-16 h-16 mr-4 shrink-0' />} />

			<Calendar
				value={calendarValue as Value}
				onChange={handleDateChange}
				allowRange
				minDate={new Date()}
				maxDate={maxDate}
				locale='en'
				showPresets={false} />
		</div>
	)
}
